import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Table, Space, Modal, message, Badge, Dropdown, Tooltip, Tag, Divider, Spin } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, DownOutlined, CheckOutlined, StopOutlined } from "@ant-design/icons";
import { useLoadList, useDelete } from "../../../hooks";
import { useTableSelection } from "../../../hooks";
import { contractManagementService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import ProductFilter from "./components/ProductFilter";
import ProductDrawer from "./components/ProductDrawer";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { SERVICE_TYPE_MAP } from "../SupplierContract/constants";

const { confirm } = Modal;

const ProductList = () => {
    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20, // 与后端默认值保持一致
    });
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [drawerMode, setDrawerMode] = useState("add"); // 'add' 或 'edit'

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("products", contractManagementService.product.getProductList, {
        page: pagination.current,
        page_size: pagination.pageSize, // 修复：使用page_size而不是pageSize
        ...filterParams,
    });

    // 使用自定义Hook处理表格选择
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection, handleRowSelectionChange } = useTableSelection({
        rowKey: "id",
        dataSource: data?.items || [],
        pagination,
    });

    // 删除产品的Hook
    const deleteMutation = useDelete("products", contractManagementService.product.deleteProduct, {
        onSuccess: () => {
            message.success("删除成功");
            refetch();
        },
    });

    // 服务类型映射（复用 settlement STANDARD_COST_TYPE）
    const serviceTypeMap = SERVICE_TYPE_MAP;

    // 渲染标签
    const renderTags = (tags) => {
        if (!tags || tags.length === 0) {
            return <Tag color="default">无标签</Tag>;
        }

        return (
            <Space size={4} wrap>
                {tags.slice(0, 3).map((tag, index) => (
                    <Tag key={index} color="blue" size="small">
                        {tag}
                    </Tag>
                ))}
                {tags.length > 3 && (
                    <Tooltip title={tags.slice(3).join(", ")}>
                        <Tag color="default" size="small">
                            +{tags.length - 3}
                        </Tag>
                    </Tooltip>
                )}
            </Space>
        );
    };

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "产品名称",
            dataIndex: "name",
            key: "name",
            width: 200,
            ellipsis: true,
        },
        {
            title: "产品编码",
            dataIndex: "code",
            key: "code",
            width: 120,
        },
        {
            title: "服务类型",
            dataIndex: "service_type",
            key: "service_type",
            width: 120,
            render: (type) => {
                const typeInfo = serviceTypeMap[type] || { text: type, color: "default" };
                return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
            },
        },
        {
            title: "单位",
            dataIndex: "unit",
            key: "unit",
            width: 80,
        },
        {
            title: "产品描述",
            dataIndex: "description",
            key: "description",
            width: 250,
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <span>{text}</span>
                </Tooltip>
            ),
        },
        {
            title: "标签",
            dataIndex: "tags",
            key: "tags",
            width: 200,
            render: renderTags,
        },
        {
            title: "状态",
            dataIndex: "is_active",
            key: "is_active",
            width: 100,
            render: (is_active) => <Badge className="status-badge" status={is_active ? "success" : "error"} text={is_active ? "启用" : "停用"} />,
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 180,
            render: (text) => formatDateTime(text),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 150,
            render: (_, record) => (
                <Space size="small" className="table-actions">
                    <PermissionGuard permission="product:read">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(record);
                            }}
                        >
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="product:delete">
                        <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(record);
                            }}
                        >
                            删除
                        </Button>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    // 全选当前页面所有产品
    const handleSelectAllVisible = useCallback(() => {
        const visibleKeys = (data?.items || []).map((item) => item.id);
        handleRowSelectionChange(visibleKeys, data?.items || []);
    }, [data?.items, handleRowSelectionChange]);

    // 处理表格分页变化
    const handleTableChange = useCallback((newPagination, filters, sorter) => {
        setPagination(newPagination);
    }, []);

    // 处理搜索
    const handleSearch = useCallback((values) => {
        const params = {};

        if (values.name) {
            params.search = values.name; // 修复：后端使用search参数而不是name
        }

        if (values.service_type) {
            params.service_type = values.service_type;
        }

        if (values.is_active !== undefined) {
            params.is_active = values.is_active;
        }

        if (values.tags && values.tags.length > 0) {
            params.tags = values.tags.join(",");
        }

        setFilterParams(params);
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    // 处理重置
    const handleReset = useCallback(() => {
        setFilterParams({});
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    // 处理新增
    const handleAdd = () => {
        setCurrentProduct(null);
        setDrawerMode("add");
        setDrawerVisible(true);
    };

    // 处理编辑
    const handleEdit = useCallback((record) => {
        console.log("=== ProductList handleEdit ===");
        console.log("点击编辑的record:", record);
        console.log("record.id:", record.id);

        // 确保数据完整性检查
        if (!record || !record.id) {
            console.error("编辑失败：record 数据不完整", record);
            message.error("数据加载失败，请刷新页面重试");
            return;
        }

        // 关闭抽屉（如果已打开）
        setDrawerVisible(false);
        
        // 使用 setTimeout 确保状态重置后再设置新数据
        setTimeout(() => {
            setCurrentProduct(record);
            setDrawerMode("edit");
            setDrawerVisible(true);
            
            console.log("=== 设置状态完成 ===");
            console.log("currentProduct将被设置为:", record);
        }, 100);
    }, []);

    // 处理删除
    const handleDelete = (record) => {
        confirm({
            title: "确认删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除产品 "${record.name}" 吗？`,
            okText: "确定",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                deleteMutation.mutate(record.id);
            },
        });
    };

    // 处理批量删除
    const handleBatchDelete = () => {
        if (selectedRowKeys.length === 0) {
            message.warning("请选择要删除的产品");
            return;
        }

        confirm({
            title: "确认批量删除",
            icon: <ExclamationCircleOutlined />,
            content: `确定要删除选中的 ${selectedRowKeys.length} 个产品吗？`,
            okText: "确定",
            okType: "danger",
            cancelText: "取消",
            onOk() {
                // 批量删除逻辑 - 改进错误处理
                Promise.all(selectedRowKeys.map((id) => contractManagementService.product.deleteProduct(id)))
                    .then(() => {
                        message.success("批量删除成功");
                        refetch();
                        clearSelection();
                    })
                    .catch((error) => {
                        console.error("批量删除失败:", error);
                        // 使用API规范的错误信息
                        const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || "批量删除失败";
                        message.error(errorMsg);
                    });
            },
        });
    };

    // 处理批量激活
    const handleBatchActivate = () => {
        if (selectedRowKeys.length === 0) {
            message.warning("请选择要激活的产品");
            return;
        }

        Promise.all(selectedRowKeys.map((id) => contractManagementService.product.activateProduct(id)))
            .then(() => {
                message.success("批量激活成功");
                refetch();
                clearSelection();
            })
            .catch((error) => {
                console.error("批量激活失败:", error);
                const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || "批量激活失败";
                message.error(errorMsg);
            });
    };

    // 处理批量停用
    const handleBatchDeactivate = () => {
        if (selectedRowKeys.length === 0) {
            message.warning("请选择要停用的产品");
            return;
        }

        Promise.all(selectedRowKeys.map((id) => contractManagementService.product.deactivateProduct(id)))
            .then(() => {
                message.success("批量停用成功");
                refetch();
                clearSelection();
            })
            .catch((error) => {
                console.error("批量停用失败:", error);
                const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || "批量停用失败";
                message.error(errorMsg);
            });
    };

    // 处理抽屉成功
    const handleDrawerSuccess = () => {
        setDrawerVisible(false);
        setCurrentProduct(null);
        refetch();
    };

    // 如果认证信息还在加载中，显示加载状态
    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载用户信息...</p>
                    </div>
                </Card>
            </div>
        );
    }

    // 权限检查 - 如果用户没有查看产品的权限，显示无权限提示
    if (!hasPermission("product:read")) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <h3>无权限访问</h3>
                        <p>您没有查看产品的权限，请联系管理员。</p>
                        <p style={{ color: "#666", fontSize: "12px", marginTop: 16 }}>
                            当前用户: {user?.username || "未知"} | 权限列表: {user?.permissions?.length ? user.permissions.join(", ") : "无权限"}
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="page-container">
            {/* 页面标题区域 - 参考Monitor页面布局 */}
            <div className="page-header-compact">
                <div className="page-title-compact">产品管理</div>
                <div className="page-action-compact">
                    <PermissionGuard permission="product:create">
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                            新增产品
                        </Button>
                    </PermissionGuard>
                </div>
            </div>

            {/* 搜索过滤 */}
            <Card className="filter-card">
                <ProductFilter onSearch={handleSearch} onReset={handleReset} />
            </Card>

            {/* 产品列表 */}
            <Card className="data-card">
                <div style={{ position: "relative" }}>
                    {/* 批量操作工具栏 - 参考BusinessPartner的实现 */}
                    {selectedRowKeys.length > 0 && (
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                zIndex: 10,
                                padding: "6px 12px",
                                background: "linear-gradient(90deg, rgba(245, 245, 245, 1) 0%, rgba(245, 245, 245,1) 56%, rgba(245, 245, 245, 0) 60%)",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                                pointerEvents: "none", // 确保右侧透明区域不阻挡鼠标操作
                            }}
                        >
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", width: "60%", pointerEvents: "auto" }}>
                                <span style={{ marginRight: 8, fontSize: "12px" }}>
                                    已选择 <span style={{ fontWeight: "bold" }}>{selectedRowKeys.length}</span> 个产品
                                </span>
                                <Divider type="vertical" style={{ margin: "0 4px" }} />
                                <Button size="small" type="link" onClick={handleSelectAllVisible} style={{ padding: "0 4px" }}>
                                    全选当前页
                                </Button>
                                <Divider type="vertical" style={{ margin: "0 4px" }} />
                                <Button size="small" type="link" onClick={clearSelection} style={{ padding: "0 4px" }}>
                                    取消选择
                                </Button>
                                <Divider type="vertical" style={{ margin: "0 4px" }} />
                                <PermissionGuard permission="product:update">
                                    <Button size="small" type="primary" ghost icon={<CheckOutlined />} onClick={handleBatchActivate} style={{ fontSize: "12px", marginRight: "8px" }}>
                                        批量激活
                                    </Button>
                                </PermissionGuard>
                                <PermissionGuard permission="product:update">
                                    <Button size="small" type="primary" ghost icon={<StopOutlined />} onClick={handleBatchDeactivate} style={{ fontSize: "12px", marginRight: "8px" }}>
                                        批量停用
                                    </Button>
                                </PermissionGuard>
                                <PermissionGuard permission="product:delete">
                                    <Button size="small" danger ghost icon={<DeleteOutlined />} onClick={handleBatchDelete} style={{ fontSize: "12px" }}>
                                        批量删除
                                    </Button>
                                </PermissionGuard>
                            </div>
                        </div>
                    )}

                    {/* 表格 */}
                    <Table
                        size="small"
                        ref={tableRef}
                        columns={columns}
                        dataSource={data?.items || []}
                        loading={isLoading}
                        rowKey="id"
                        rowSelection={
                            hasPermission("product:update")
                                ? {
                                      ...rowSelection,
                                      columnWidth: 6, // 明确设置选择框列宽
                                  }
                                : null
                        }
                        pagination={{
                            ...pagination,
                            total: data?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `共 ${total} 条记录`,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 1200 }}
                        onRow={getRowProps}
                    />
                </div>
            </Card>

            {/* 产品编辑抽屉 */}
            <ProductDrawer
                key={currentProduct?.id || 'new'}
                visible={drawerVisible}
                mode={drawerMode}
                initialValues={currentProduct}
                onSuccess={handleDrawerSuccess}
                onCancel={() => {
                    setDrawerVisible(false);
                    setCurrentProduct(null);
                }}
            />
        </div>
    );
};

export default ProductList;
